import {debounce, throttle} from 'throttle-debounce';
import CustomSelect from 'components/forms/custom-select';
import ParametricFilterFeatures from './features';
import ParametricFilterMortgage from './mortgage';
import ParametricFilterPopup from './popup';
import RangeSlider from 'components/range-slider';
import {Utils} from 'common/scripts/utils';

class ParametricFilter {
    constructor(options) {
        /**
         * Параметры
         */
        this.options = options;

        /**
         * Целевой элемент
         */
        this.element = options.element ? options.element.querySelector('.j-parametric-filter') : null;

        /**
         * CSS-класс состояния загрузки
         * @type {string}
         */
        this.loadingClass = 'is-loading';

        /**
         * CSS-класс неактивного состояния
         * @type {string}
         */
        this.disableClass = 'is-disabled';

        /**
         * CSS-класс фиксированного поиска
         * @type {string}
         */
        this.fixedClass = 'is-fixed';

        /**
         * CSS-класс фиксированного поиска в области скролла
         * @type {string}
         */
        this.scrollClass = 'is-scroll';

        /**
         * Класс для элемента фильтра в состоянии загрузки
         * @type {string}
         */
        this.elemLoadingClass = 'is-filter-elem-load';

        /**
         * Задержка отложенных функций
         * @type {number}
         */
        this.debTime = 300;

        /**
         * Высота экрана
         * @type {Number}
         */
        this.windowHeight = Utils.getWindowHeight();

        /**
         * Разрешение, на котором фиксируется фильтр
         * @type {boolean}
         */
        this.isDesktiop = !Utils.isMobile();

        /**
         * Тип последнего запроса
         * @type {string}
         */
        this.reqType = '';
    }

    init() {
        if (!this.element) {
            return;
        }

        this._bindContext();
        this._initElements();
        this._initSelects();
        this._initSliders();

        this._initPopup();
        this._initMortgage();
        this._initFeatures();
        this._setFilterBehavior();

        this._bindEvents();
    }

    _bindContext() {
        this._debouncedScrollListener = debounce(6, this._setScrollFixed.bind(this));
    }

    /**
     * Инит элементов
     * @private
     */
    _initElements() {
        this.form = this.element.querySelector('.j-parametric-filter__form');
        this.reset = this.element.querySelector('.j-parametric-filter__reset');
        this.submit = this.element.querySelector('.j-parametric-filter__submit');
        this.submitText = this.element.querySelector('.j-parametric-filter__submit-text');
        this.submitLink = this.element.querySelector('.j-parametric-filter__submit-link');
        this.filterElems = Array.from(this.element.querySelectorAll('.j-parametric-filter__elem'));
        this.content = this.element.querySelector('.j-parametric-filter__content');

        if (this.submitLink) {
            this.href = this.submitLink.getAttribute('href').split('?')[0];
        }
    }

    /**
     * Инициализация селектов
     * @private
     */
    _initSelects() {
        this.selectElements = Array.from(this.element.querySelectorAll('.j-parametric-filter__select'));
        this.selects = [];
        this.selectElements.forEach((element) => {
            const select = new CustomSelect({
                elem        : element,
                onGroupClick: debounce(this.debTime, this._send.bind(this, 'filter'))
            });

            select.init();
            this.selects.push(select);
        });
    }

    /**
     * Инициализация рейндж-слайдеров
     * @private
     */
    _initSliders() {
        this.rangeElements = Array.from(this.element.querySelectorAll('.j-range-slider'));
        this.ranges = {};
        this.rangeElements.forEach((element) => {
            const range = new RangeSlider();

            range.init({
                wrapper : element,
                onFinish: debounce(this.debTime, this._send.bind(this, 'filter'))
            });

            this.ranges[element.dataset.slider] = range;
        });
    }

    /**
     * Инит мобильного попапа
     * @private
     */
    _initPopup() {
        this.popup = new ParametricFilterPopup(this.element);
        this.popup.init();
    }

    /**
     * Инициализация блока ипотеки
     * @private
     */
    _initMortgage() {
        const mortgage = this.element.querySelector('.j-parametric-filter__mortgage');

        if (!mortgage) {
            return;
        }

        this.mortgage = new ParametricFilterMortgage({
            element: mortgage,
            parent : this.element,
            update : () => {
                this._setFilterHeight();
            }
        });

        this.mortgage.init();
    }

    /**
     * Инициализация блока особенностей
     * @private
     */
    _initFeatures() {
        const features = this.element.querySelector('.j-parametric-filter__features');

        if (!features) {
            return;
        }

        this.features = new ParametricFilterFeatures({
            element: features,
            parent : this.element,
            update : () => {
                this._setFilterHeight();
            }
        });

        this.features.init();
    }

    /**
     * Вешает обработчики событий
     * @private
     */
    _bindEvents() {
        const events = ['resize', 'orientationchange'];

        events.forEach((event) => {
            window.addEventListener(event, this._onResize.bind(this));
        });

        this.form.addEventListener('submit', (event) => {
            event.preventDefault();
        });
        this.form.addEventListener('submit', throttle(1000, this._send.bind(this, 'show')));
        // eslint-disable-next-line
        this.form.addEventListener('change', debounce(this.debTime, this._send.bind(this, 'filter')));
        this.reset.addEventListener('click', debounce(this.debTime, this._send.bind(this, 'reset')));

        this.filterElems.forEach((elem) => {
            elem.addEventListener('click', () => {
                this.lastFilterElem = elem;
            });
        });
    }

    _onResize() {
        this.isDesktiop = !Utils.isMobile();
        this.windowHeight = Utils.getWindowHeight();
        this._setFilterBehavior();
    }

    _setFilterBehavior() {
        this.currentDelta = 0;
        this.lastScrollPosition = 0;
        this._setFilterHeight();
    }

    _setFilterHeight() {
        this.filterHeight = this.content.getBoundingClientRect().height;
        this._setFilterScrollEvents();
    }

    /**
     * Устанавливает скроллящийся фильтр для узких экранов
     * @private
     */
    _setFilterScrollEvents() {
        if (this.isDesktiop && this.filterHeight > this.windowHeight) {
            this.content.classList.add(this.fixedClass);
            window.addEventListener('scroll', this._debouncedScrollListener);
        } else {
            this.content.classList.remove(this.fixedClass, this.scrollClass);
            this.content.style.top = '';
            window.removeEventListener('scroll', this._debouncedScrollListener);
        }
    }

    /**
     * Навешивает класс для фильтра при скролле
     * @private
     */
    _setScrollFixed() {
        const topBorder = this.element.getBoundingClientRect().top;
        const bottomBorder = topBorder + this.element.getBoundingClientRect().height;
        const deltaTop = this.windowHeight - this.filterHeight;
        const currentScroll = Utils.getScrollTop();
        const scrollValue = this.lastScrollPosition - currentScroll;

        if (topBorder <= 0 && bottomBorder >= this.windowHeight) {
            this.content.classList.add(this.scrollClass);

            if (currentScroll > this.lastScrollPosition) {
                // down
                this.currentDelta = this.currentDelta + scrollValue >= deltaTop ?
                    this.currentDelta + scrollValue :
                    deltaTop;
            } else {
                // up
                this.currentDelta = this.currentDelta + scrollValue < 0 ?
                    this.currentDelta + scrollValue :
                    0;
            }
        } else {
            this.content.classList.remove(this.scrollClass);
        }

        this.content.style.top = `${this.currentDelta}px`;
        this.lastScrollPosition = currentScroll <= 0 ? 0 : currentScroll;
    }

    _getAdditionalData() {
        const data = {};

        this.selects.forEach((select) => {
            Object.assign(data, select.getGroupOptions());
        });

        return data;
    }

    /**
     * Отправка данных
     * @param {string} reqType - тип запроса
     * @private
     */
    _send(reqType) {
        if (reqType === 'reset' || reqType === 'show') {
            this.lastFilterItem = null;
        }

        if (this.options.change) {
            const data = reqType === 'reset' ? null : this._getAdditionalData();

            this.options.change(reqType, data);
        }
    }

    /**
     * Обновляет фильтры
     * @param {object} filters - данные с сервера
     * @private
     */
    _updateFilters(filters) {
        this._updateChecks(filters);
        this.selects.forEach((select) => {
            select.update(this.reqType === 'reset' || this.reqType === 'resetFull');
        });

        if (this.features) {
            this.features.update();
        }

        if (this.mortgage) {
            this.mortgage.update();
        }
    }

    /**
     * Обновление чекбоксов
     * @param {object} data - данные для обработки
     * @private
     */
    _updateChecks(data) {
        Object.entries(data).forEach(([name, options]) => {
            const filter = this.element.querySelector(`[data-filter="${name}"]`);

            if (!filter) {
                return;
            }

            options.forEach((option) => {
                const input = filter.querySelector(`input[value="${option.value}"]`);

                if (!input) {
                    return;
                }

                input.disabled = option.disabled;
                input.checked = option.checked;
            });
        });
    }

    /**
     * Обновляет рейндж-слайдеры после того, как пришли новые данные с сервера
     * @param {object} ranges - данные с сервера
     * @private
     */
    _updateRanges(ranges) {
        Object.entries(ranges).forEach(([key, data]) => {
            const range = this.ranges[key];

            if (range) {
                range.update(data);
            }
        });
    }

    /**
     * Обновляет кнопку фильтра по данным с сервера
     * @param {string} count - строка с количеством найденных квартир
     * @private
     */
    _updateSubmit(count) {
        this.submit.classList.toggle(this.disableClass, !count);
        this.submit[count ? 'removeAttribute' : 'setAttribute']('disabled', '');

        this._replaceContent(this.submitText, count || 'Вариантов не найдено');
    }

    /**
     * Заменяет контент в блоке на переданный
     * @param {Element} element - целевой блок
     * @param {string} html - контент, который будет подставлен
     * @private
     */
    _replaceContent(element, html) {
        Utils.clearHtml(element);

        element.insertAdjacentHTML('beforeend', html);
    }

    /**
     * Применяет данные с сервера
     * @param {object} data - данные с сервера
     * @param {string} reqType - тип произошедшего запроса
     */
    setFilter(data, reqType) {
        this.reqType = reqType;
        this._updateSubmit(data.count);

        if (data.filters) {
            this._updateFilters(data.filters);
        }

        if (data.sliders) {
            this._updateRanges(data.sliders);
        }
    }

    /**
     * Устанавливает ссылке href на результаты поиска
     * @param {String} searchParams - гет параметры итоговой формдаты
     */
    setHref(searchParams) {
        if (!this.submitLink || !searchParams) {
            return;
        }

        this.submitLink.setAttribute('href', `${this.href}?${searchParams}`);
    }

    /**
     * Поведение при показе результатов
     */
    setResult() {
        this.popup.close();
    }

    /**
     * Запускает состояние ожидания
     * @param {boolean} isSubmitLoading - надо ли лоадить кнопку поиска
     */
    runLoading(isSubmitLoading = false) {
        this.element.classList.add(this.loadingClass);

        if (isSubmitLoading) {
            this.submit.classList.add(this.loadingClass);

            this.filterElems.forEach((elem) => {
                if (this.lastFilterElem !== elem) {
                    elem.classList.add(this.elemLoadingClass);
                }
            });
        }
    }

    /**
     * Отменяет состояние ожидания
     */
    stopLoading() {
        if (!this.element) {
            return;
        }

        this.element.classList.remove(this.loadingClass);
        this.submit.classList.remove(this.loadingClass);
        this.filterElems.forEach((elem) => {
            elem.classList.remove(this.elemLoadingClass);
        });
    }

    /**
     * Возвращает форму
     * @returns {Element} - элемент формы
     */
    getForm() {
        if (!this.element) {
            return null;
        }

        return this.form;
    }

    /**
     * Проходит по рейнджам и конвертирует значения инпутов в числа
     * @param {object} formData - формдата, в которой нужно переписать значения
     */
    convertRanges(formData) {
        if (!this.element) {
            return;
        }

        Object.values(this.ranges).forEach((range) => {
            const inputs = range.getInputs();

            inputs.forEach((input, index) => {
                const name = input.name;
                const val = Number(input.value.replace(/\D+/g, ''));

                const isChanged = index === 0 ? range.changeFrom : range.changeTo;

                if (isChanged) {
                    formData.set(name, val);
                } else {
                    formData.delete(name);
                }
            });
        });
    }
}

export default ParametricFilter;
